import { useMemo, useState, type ReactNode } from 'react';
import type { Person } from '@domain/entities/Person';
import { effectivePermissions } from '@domain/policies/RbacPolicy';
import {
  DEFAULT_ROLE_PERMISSIONS,
  PERMISSION_CATALOG,
  ROLE_LABEL,
  isPermission,
  type Permission,
} from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import { Banner, Button, Modal, cx } from '../../design-system';
import { reasonFor } from '../../format';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import { useToast } from '../../providers/ToastProvider';
import styles from './PermissionsAction.module.css';

/**
 * Los permisos de esta persona, por encima o por debajo de su rol.
 *
 * El rol reparte el paquete que corresponde a un trabajo, y para casi todo el
 * mundo eso basta. Lo que el modelo no sabía decir era la excepción, que en 338
 * personas es constante: «RR.HH., y **además** ve sueldos», «administradora de
 * área, **pero no** expedientes». La única alternativa era ampliar el rol, y
 * ampliarlo se lo amplía a todos los que lo tienen.
 *
 * **La pantalla enseña las tres cosas a la vez** —lo que da el rol, lo que se
 * añadió y lo que se quitó— porque la pregunta que trae aquí a alguien nunca es
 * «qué permisos tiene» sino «por qué éste sí y aquél no». Una lista de casillas
 * marcadas no contesta eso: hay que ver de dónde viene cada marca.
 */
export const PermissionsAction = ({
  person,
  canChange,
  onDone,
}: {
  readonly person: Person;
  readonly canChange: boolean;
  readonly onDone: () => Promise<void>;
}): ReactNode => {
  const config = useRuntimeConfig();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const delRol = useMemo(
    () => config.rolePermissions[person.role] ?? DEFAULT_ROLE_PERMISSIONS[person.role],
    [config.rolePermissions, person.role],
  );

  const [extra, setExtra] = useState<ReadonlySet<Permission>>(
    new Set(person.extraPermissions.filter(isPermission)),
  );
  const [revoked, setRevoked] = useState<ReadonlySet<Permission>>(
    new Set(person.revokedPermissions.filter(isPermission)),
  );

  const efectivos = useMemo(
    () =>
      new Set(
        effectivePermissions(config.rolePermissions, person.role, {
          extra: [...extra],
          revoked: [...revoked],
        }),
      ),
    [config.rolePermissions, person.role, extra, revoked],
  );

  const excepciones = extra.size + revoked.size;

  /*
   * Un clic recorre los tres estados posibles de un permiso, y cuáles son depende
   * de si el rol ya lo da: sobre lo que da el rol se puede quitar y devolver;
   * sobre lo que no da, conceder y retirar. Ofrecer «revocar» un permiso que el
   * rol no concede sería ofrecer una operación sin efecto.
   */
  const alternar = (permiso: Permission): void => {
    const loDaElRol = delRol.includes(permiso);

    if (loDaElRol) {
      setRevoked((actual) => {
        const siguiente = new Set(actual);
        if (siguiente.has(permiso)) siguiente.delete(permiso);
        else siguiente.add(permiso);
        return siguiente;
      });
      return;
    }

    setExtra((actual) => {
      const siguiente = new Set(actual);
      if (siguiente.has(permiso)) siguiente.delete(permiso);
      else siguiente.add(permiso);
      return siguiente;
    });
  };

  const guardar = async (): Promise<void> => {
    setBusy(true);
    const result = await getServices().people.setPermissions({
      personId: person.id,
      extraPermissions: [...extra],
      revokedPermissions: [...revoked],
    });
    setBusy(false);

    if (!result.ok) {
      toast.bad(`No se pudieron guardar los permisos.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(
      excepciones === 0
        ? `${person.displayName} vuelve a tener exactamente lo de su rol.`
        : `Permisos de ${person.displayName} actualizados.`,
    );
    setOpen(false);
    await onDone();
  };

  if (!canChange) return null;

  return (
    <>
      {/*
        Un botón como sus vecinos, no una tarjeta.
        Iba en una caja con título y una línea de explicación, y en una fila de
        cinco botones planos eso no se lee como uno más: se lee como un aviso. Lo
        que de verdad hacía falta a este nivel era saber si esta persona tiene
        excepciones —el resto lo cuenta el modal—, y para eso basta un número.
      */}
      <Button
        variant="secondary"
        onClick={() => {
          setOpen(true);
        }}
      >
        Permisos
        {excepciones > 0 && (
          <span className={styles.badge} title={`${String(excepciones)} sobre su rol`}>
            {excepciones}
          </span>
        )}
      </Button>

      {open && (
        <Modal
          title={`Permisos de ${person.displayName}`}
          size="wide"
          onClose={() => {
            setOpen(false);
          }}
          footer={
            <>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                busy={busy}
                onClick={() => {
                  void guardar();
                }}
              >
                Guardar permisos
              </Button>
            </>
          }
        >
          <div className={styles.body}>
            <p className={styles.lead}>
              Su rol es <strong>{ROLE_LABEL[person.role]}</strong> y de ahí sale lo marcado en
              azul. Lo que le añadas o le quites vale sólo para esta persona: el rol no se
              toca, y los demás que lo tengan siguen igual.
            </p>

            {/*
              Las reglas no leen esto, y callarlo sería prometer de más. Un permiso
              concedido aquí gobierna las callables y lo que la pantalla ofrece; las
              lecturas directas siguen con el piso grueso del rol.
            */}
            <Banner tone="info">
              Esto decide qué puede <strong>hacer</strong> y qué le ofrece el portal. Para lo
              que puede <strong>leer</strong> directo —el sueldo, el expediente— el piso lo
              sigue poniendo su rol.
            </Banner>

            <ul className={styles.grid}>
              {/* Del catálogo y no de `PERMISSIONS`: trae la etiqueta y el grupo,
                  que es lo que hace legible una lista de veinte claves. */}
              {PERMISSION_CATALOG.map(({ key: permiso, label, note }) => {
                const loDaElRol = delRol.includes(permiso);
                const anadido = extra.has(permiso);
                const quitado = revoked.has(permiso);
                const activo = efectivos.has(permiso);

                return (
                  <li key={permiso}>
                    <button
                      type="button"
                      className={cx(
                        styles.perm,
                        activo && styles.permOn,
                        anadido && styles.permAdded,
                        quitado && styles.permRemoved,
                      )}
                      aria-pressed={activo}
                      onClick={() => {
                        alternar(permiso);
                      }}
                    >
                      <span className={styles.permName}>{label}</span>
                      <span className={styles.permNote}>{note}</span>
                      <span className={styles.permSource}>
                        {quitado
                          ? 'quitado a esta persona'
                          : anadido
                            ? 'añadido a esta persona'
                            : loDaElRol
                              ? 'de su rol'
                              : 'no lo tiene'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </Modal>
      )}
    </>
  );
};
